import { HttpErrorResponse } from '@angular/common/http';
import { computed, inject, Injectable, Signal } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import {
  catchError,
  combineLatest,
  combineLatestWith,
  distinctUntilChanged,
  filter,
  map,
  Observable,
  of,
  shareReplay,
  startWith,
  Subject,
  switchMap,
  take
} from 'rxjs';
import { AuthFirebaseApiService } from '@bpp-stella/api/auth/api/auth-firebase-api.service';
import { PersonFirebaseInfoDto } from '@bpp-stella/api/auth/model/person-firebase-info-dto.model';
import { BaseStateService } from '@bpp-stella/common/state/base-state.service';
import { RegistrationService } from '@bpp-stella/common/state/auth/registration.service';
import { AuthDestination, resolveAuthDestination } from './auth-destination';
import { AuthUser } from './auth-user.model';
import { FirebaseAuthService } from './firebase-auth.service';

export interface AuthSessionState {
  user: AuthUser | null;
  person: PersonFirebaseInfoDto | null;
  isLoaded: boolean;
  error: number | null;
}

/**
 * The single source of truth for "who is signed in and are they a known Stella person" —
 * replaces the click-dummy placeholder (`features/auth/state/auth-state.service.ts`) once the
 * real pages exist (Task 4).
 */
@Injectable({ providedIn: 'root' })
export class AuthSessionService implements BaseStateService {
  private readonly firebaseAuth = inject(FirebaseAuthService);
  private readonly authFirebaseApi = inject(AuthFirebaseApiService);
  private readonly registration = inject(RegistrationService);
  private readonly reloadSubject = new Subject<void>();

  public readonly state$: Observable<AuthSessionState> = this.firebaseAuth.currentUser$.pipe(
    combineLatestWith(this.reloadSubject.pipe(startWith(undefined))),
    switchMap(([user]) => {
      if (!user) {
        return of({ user: null, person: null, isLoaded: false, error: null });
      }
      return this.authFirebaseApi.apiAuthFirebaseLoginGet().pipe(
        map((person) => ({ user, person, isLoaded: true, error: null })),
        catchError((error: HttpErrorResponse) => of({ user, person: null, isLoaded: true, error: error.status })),
        // Synchronous loading marker: the guard/routing pipeline must see "pending", not "signed out".
        startWith({ user, person: null, isLoaded: false, error: null })
      );
    }),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  /** `undefined` = auth state unknown yet (nothing emitted by Firebase's listener). */
  public readonly state: Signal<AuthSessionState | undefined> = toSignal(this.state$);
  public readonly person: Signal<PersonFirebaseInfoDto | null> = computed(() => this.state()?.person ?? null);
  public readonly isAuthenticated: Signal<boolean> = computed(() => !!this.person());

  // `toObservable(isFlowActive)` stays in the combineLatest purely as a change trigger — its own
  // emitted value is one microtask behind the signal (effects are scheduled, not synchronous), so
  // a caller that mutates the flag and immediately awaits a destination in the same tick (e.g.
  // register-done-page's resetState() -> awaitDestination()) would otherwise be resolved against
  // the stale flag. Reading `this.registration.isFlowActive()` directly inside `map` always gets
  // the current value.
  public readonly destination$: Observable<AuthDestination | 'pending'> = combineLatest([
    this.state$,
    toObservable(this.registration.isFlowActive)
  ]).pipe(
    map(([state]) => resolveAuthDestination(state, this.registration.isFlowActive())),
    distinctUntilChanged()
  );

  public awaitDestination(): Observable<AuthDestination> {
    return this.destination$.pipe(
      filter((destination): destination is AuthDestination => destination !== 'pending'),
      take(1)
    );
  }

  public resolveAfterSignIn(): Observable<AuthDestination> {
    const cached = this.state();
    if (cached?.isLoaded && cached.error !== null) {
      this.reloadAuthLogin();
    }
    return this.awaitDestination();
  }

  public reloadAuthLogin(): void {
    this.reloadSubject.next();
  }

  /**
   * Re-triggers `state$`'s switchMap. This is safe to call unconditionally after sign-out: with no
   * current user, the `combineLatestWith`/`switchMap` pipeline takes the `!user` branch (a plain
   * `of({ user: null, ... })`), so the re-trigger resolves straight to the signed-out state without
   * issuing a login GET.
   */
  public resetState(): void {
    this.reloadSubject.next();
  }

  public handleError(error: HttpErrorResponse): void {
    console.error('An error occurred in AuthSessionService:', error);
  }
}
